import { AnimationClip, type KeyframeTrack } from 'three'

/**
 * Trim an AnimationClip to the inclusive time range [start, end] (in seconds),
 * rebasing keyframe times so the new clip starts at 0. Tracks with no
 * keyframes inside the range are still kept (with two clamped boundary
 * keyframes) so the bones they animate don't pop to a default rotation when
 * the trimmed clip plays.
 *
 * Assumes:
 *  - tracks have monotonically non-decreasing `times`
 *  - keyframe values are in fixed itemSize chunks (`values.length / times.length`)
 *
 * Both assumptions hold for clips produced by Three.js's loaders, the
 * KeyframeBuilder utility, and the existing pose-bake flow.
 */

export class ClipTrimmer {
  static trim (clip: AnimationClip, start: number, end: number): AnimationClip {
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      throw new Error('ClipTrimmer.trim: start and end must be finite numbers.')
    }
    if (end <= start) {
      throw new Error('ClipTrimmer.trim: end must be greater than start.')
    }

    const trimmed_tracks: KeyframeTrack[] = []
    for (const track of clip.tracks) {
      const trimmed = trim_track(track, start, end)
      if (trimmed !== null) trimmed_tracks.push(trimmed)
    }

    const out = new AnimationClip(clip.name, end - start, trimmed_tracks, clip.blendMode)
    return out
  }
}

function trim_track (track: KeyframeTrack, start: number, end: number): KeyframeTrack | null {
  const item_size = track.values.length / track.times.length
  if (!Number.isFinite(item_size) || item_size < 1) return null

  // Collect indices of keyframes strictly inside (start, end).
  const inside_indices: number[] = []
  for (let i = 0; i < track.times.length; i++) {
    const t = track.times[i]
    if (t >= start && t <= end) inside_indices.push(i)
  }

  // Always emit boundary samples at the trim edges (interpolated) so the
  // trimmed clip starts and ends in the correct pose. If there's no
  // surrounding keyframe to interpolate from, clamp to the nearest one.
  const start_value = sample_track_value(track, start, item_size)
  const end_value = sample_track_value(track, end, item_size)

  const out_times: number[] = []
  const out_values: number[] = []

  out_times.push(0)
  push_values(out_values, start_value)

  let last_pushed_time = start
  for (const idx of inside_indices) {
    const t = track.times[idx]
    if (t === start || t === end) continue // boundary handled separately
    if (t === last_pushed_time) continue
    out_times.push(t - start)
    for (let k = 0; k < item_size; k++) {
      out_values.push(track.values[idx * item_size + k])
    }
    last_pushed_time = t
  }

  const final_time = end - start
  if (out_times[out_times.length - 1] !== final_time) {
    out_times.push(final_time)
    push_values(out_values, end_value)
  }

  // Reuse the same track constructor so interpolation type / item size carry
  // over without us having to special-case Vector vs Quaternion vs Number.
  const Constructor = track.constructor as new (
    name: string,
    times: ArrayLike<number>,
    values: ArrayLike<number>
  ) => KeyframeTrack
  return new Constructor(track.name, out_times, out_values)
}

/**
 * Sample a track at an arbitrary time. Uses linear interpolation between the
 * surrounding keyframes; clamps to the nearest keyframe outside the track's
 * range. Quaternion tracks would technically benefit from slerp here, but
 * linear lerp at a 1-frame boundary is visually indistinguishable and avoids
 * a per-track-type switch.
 */
function sample_track_value (track: KeyframeTrack, t: number, item_size: number): number[] {
  const times = track.times
  const values = track.values
  if (times.length === 0) return new Array(item_size).fill(0)

  // Find the surrounding keyframes via binary search.
  let lo = 0
  let hi = times.length - 1
  if (t <= times[0]) {
    return Array.from(values.slice(0, item_size))
  }
  if (t >= times[hi]) {
    const start_off = hi * item_size
    return Array.from(values.slice(start_off, start_off + item_size))
  }
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (times[mid] <= t) lo = mid
    else hi = mid
  }
  const t0 = times[lo]
  const t1 = times[hi]
  const alpha = t1 === t0 ? 0 : (t - t0) / (t1 - t0)
  const out: number[] = new Array(item_size)
  for (let k = 0; k < item_size; k++) {
    const v0 = values[lo * item_size + k]
    const v1 = values[hi * item_size + k]
    out[k] = v0 + (v1 - v0) * alpha
  }
  return out
}

function push_values (out: number[], values: number[]): void {
  for (const v of values) out.push(v)
}
